<template>
  <div class="rd-screen">
    <report-tools>
      <ui-segment
        v-model="groupBy"
        tabs
        :options="[
          { value: 'meal', label: $t('report_mobile.by_meal') },
          { value: 'worker', label: $t('report_mobile.by_worker') },
        ]"
        :label="$t('report_mobile.group_by')"
      />
    </report-tools>

    <report-state
      v-if="!selectedCompanies.length"
      icon="mdi-domain"
      :title="$t('no_companies_selected')"
    ></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!orderCompanies.length"
      icon="mdi-table"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <template v-else>
      <section v-for="company in orderCompanies" :key="company.id">
        <div class="rd-section">
          <h2>
            {{ company.name }}<template v-if="isSingleShift"> · {{ singleShiftLabel }}</template>
          </h2>
          <span class="rd-num">{{ workersCountText(company) }}</span>
        </div>

        <div v-if="groupBy === 'meal'" class="rd-orders">
          <article v-for="meal in company.meals" :key="meal.key" class="rd-order-card">
            <div class="rd-order-card__head">
              <b :title="meal.name">{{ meal.name }}</b>
              <span class="rd-pill">{{ meal.count }}</span>
            </div>
            <div v-for="group in meal.shifts" :key="group.key" class="rd-order-card__group">
              <div v-if="!isSingleShift" class="rd-order-card__shift">
                <report-shift-tag :shift="group.id" long></report-shift-tag>
                <i class="rd-order-card__rule"></i>
                <span class="rd-num" :title="pluralT('report_mobile.meals_n', group.count)">{{ group.count }}</span>
              </div>
              <ul>
                <li v-for="person in shownPeople(meal, group)" :key="person.key">
                  <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ person.initials }}</span>
                  <span class="rd-order-card__name">{{ person.name }}</span>
                  <span v-if="person.quantity > 1" class="rd-muted rd-num">{{ person.quantity }}×</span>
                </li>
                <li v-if="hiddenPeople(meal, group) > 0">
                  <button type="button" class="ui-text-btn" @click="expand(group.key)">
                    + {{ pluralT("report_mobile.workers_n", hiddenPeople(meal, group)) }}
                  </button>
                </li>
              </ul>
            </div>
          </article>
        </div>

        <ui-table v-else variant="report" :columns="columns" :rows="company.workers" :row-key="(worker) => worker.id">
          <template #cell-worker="{ row }">
            <span class="rd-person">
              <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
              {{ row.name }}
            </span>
          </template>

          <template #cell-meal="{ row }">
            <span v-for="item in row.items" :key="item.key" class="rd-line rd-line--shift">
              <report-shift-tag v-if="!isSingleShift" :shift="item.shiftId"></report-shift-tag>
              <span>{{ item.quantity }}× {{ item.meal }}</span>
            </span>
          </template>
        </ui-table>
      </section>
    </template>
  </div>
</template>

<script>
import ReportMealsTabMealEmployee from "@/components/report/meals/tabs/ReportMealsTabMealEmployee.vue";
import hasOrderGroups from "../../shared/hasOrderGroups";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";

const PEOPLE_PREVIEW = 6;
// Kad jelo ima više smena, svaka grupa pokazuje manje redova, da kartica ne naraste previše.
const GROUP_PREVIEW = 3;

/**
 * Narudžbe radnika na računaru: po jelu (kartice sa spiskom radnika, razvrstanim po smenama) ili po
 * radniku (tabela, uz svaku stavku smena).
 * Nasleđuje učitavanje, pretragu, štampu i Excel iz ReportMealsTabMealEmployee.
 */
export default {
  name: "ReportDesktopWorkerOrders",
  extends: ReportMealsTabMealEmployee,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasOrderGroups],
  components: { ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    opened: {},
  }),
  computed: {
    /** Kolone tabele "po radniku" (iste za svaku firmu). */
    columns() {
      return [
        { key: "worker", label: this.$t("worker"), cellCls: "is-name" },
        { key: "meal", label: this.$t("meal") },
      ];
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    workerTotal() {
      return this.orderCompanies.reduce((sum, company) => sum + company.workers.length, 0);
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.workerTotal, tone: "inverse" },
        { key: "companies", label: this.$t("companies"), value: this.orderCompanies.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.orderCompanies.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    shownPeople(meal, group) {
      if (this.opened[group.key] || this.filters.search) {
        return group.people;
      }
      return group.people.slice(0, meal.shifts.length > 1 ? GROUP_PREVIEW : PEOPLE_PREVIEW);
    },
    hiddenPeople(meal, group) {
      return group.people.length - this.shownPeople(meal, group).length;
    },
    expand(key) {
      this.opened = { ...this.opened, [key]: true };
    },
  },
};
</script>
